import type { AxiosProgressEvent } from 'axios';
import { tenantApi } from '@/modules/tenant/services/tenantApi';
import type { InventoryMediaItem } from '../types/boat.types';

/**
 * #1219 — an AI-intake upload the image classifier has not routed yet. It is NOT
 * in the public gallery (`images`) and has no storage URL: `preview_url` is the
 * auth-gated stream (the session cookie authorizes it per request).
 */
export interface PendingBoatImage {
  id: number;
  file_name: string;
  mime_type: string;
  size: number;
  preview_url: string;
  order: number | null;
}

export interface BoatMediaResponse {
  images: InventoryMediaItem[];
  videos: InventoryMediaItem[];
  /** #1219 — held AI-intake images (absent from a cache seeded before #1219). */
  pending_images?: PendingBoatImage[];
}

/** #1219 — POST /boats/{id}/media/pending/classify (202). */
export interface ClassifyPendingResult {
  /** `skipped_no_permission` is client-only: useClassifyPendingImages without inventory.ai.generate (#1458). */
  status: 'dispatched' | 'skipped_ai_disabled' | 'skipped_no_media' | 'skipped_no_permission';
  /** The whole intake batch the run reads, not the held count. */
  media_count: number;
}

export const boatMediaApi = {
  getMedia: (boatId: string) =>
    tenantApi.getOne<BoatMediaResponse>(`/boats/${boatId}/media`),

  /**
   * Upload a single file with optional progress reporting. Preferred over
   * uploadMedia() for the dropzone path — sending one file per request
   * lets the orchestrator hook show per-tile progress and cap concurrency.
   */
  uploadMediaFile: (
    boatId: string,
    type: 'image' | 'video',
    file: File,
    onUploadProgress?: (event: AxiosProgressEvent) => void,
  ) => {
    const formData = new FormData();
    formData.append('type', type);
    formData.append('media[]', file);

    return tenantApi.postOne<BoatMediaResponse>(
      `/boats/${boatId}/media`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress,
      },
    );
  },

  /**
   * Batch upload — kept for callers that don't need progress reporting.
   * The dropzone path uses uploadMediaFile() instead.
   */
  uploadMedia: (boatId: string, type: 'image' | 'video', files: File[]) => {
    const formData = new FormData();
    formData.append('type', type);
    files.forEach((file) => {
      formData.append('media[]', file);
    });

    return tenantApi.postOne<BoatMediaResponse>(
      `/boats/${boatId}/media`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
  },

  deleteMedia: (boatId: string, mediaId: number) =>
    tenantApi.deleteOne(`/boats/${boatId}/media/${mediaId}`),

  setPrimaryImage: (boatId: string, mediaId: number) =>
    tenantApi.postOne<{ images: InventoryMediaItem[] }>(
      `/boats/${boatId}/media/${mediaId}/primary`,
    ),

  reorderMedia: (boatId: string, mediaIds: number[]) =>
    tenantApi.postAction(`/boats/${boatId}/media/reorder`, {
      media_ids: mediaIds,
    }),

  /**
   * #1219 — move one held image into the gallery (dealer confirmation). A 404
   * means the image is no longer held (the classifier promoted it first).
   */
  confirmPendingPhoto: (boatId: string, mediaId: number) =>
    tenantApi.postOne<BoatMediaResponse>(`/boats/${boatId}/media/${mediaId}/confirm-photo`),

  /** #1219 — re-run the boat's whole intake batch (gallery ∪ documents ∪ held). */
  classifyPendingImages: (boatId: string) =>
    tenantApi.postOne<ClassifyPendingResult>(`/boats/${boatId}/media/pending/classify`),
};
